import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Radio, RotateCcw, Sparkles } from 'lucide-react';

const DLC_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

const sanitizeHexByte = (value) => value.replace(/[^0-9a-fA-F]/g, '').slice(0, 2);
const sanitizeHexId = (value) => value.replace(/[^0-9a-fA-F]/g, '').slice(0, 8);

const CanFrameEditor = ({ canIdHex, onCanIdChange, dlc, onDlcChange, dataBytes, onByteChange, onReset, onLoadExample }) => {
  return (
    <Card className="technical-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Radio className="h-5 w-5 text-autoxium-blue" />
            CAN Frame
          </CardTitle>
          <CardDescription>
            Set the CAN ID, DLC, and raw data bytes. The bit grid below resizes to match the DLC.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onLoadExample} data-testid="can-endianness-load-example-btn">
            <Sparkles className="h-3.5 w-3.5" /> Load Example
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onReset} data-testid="can-endianness-reset-btn">
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4 max-w-md">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">CAN ID (hex)</Label>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-sm text-muted-foreground">0x</span>
              <Input
                value={canIdHex}
                onChange={(e) => onCanIdChange(sanitizeHexId(e.target.value).toUpperCase())}
                placeholder="123"
                className="font-mono"
                data-testid="can-endianness-can-id-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">DLC (bytes)</Label>
            <Select value={String(dlc)} onValueChange={(v) => onDlcChange(Number(v))}>
              <SelectTrigger data-testid="can-endianness-dlc-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DLC_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} byte{d === 1 ? '' : 's'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Data Bytes</Label>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: dlc }, (_, i) => (
              <div key={i} className="space-y-1 text-center">
                <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">Byte {i}</div>
                <Input
                  value={dataBytes[i]}
                  onChange={(e) => onByteChange(i, sanitizeHexByte(e.target.value).toUpperCase())}
                  className="h-9 w-12 text-center font-mono text-sm px-1"
                  data-testid={`can-endianness-byte-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CanFrameEditor;
